import { useEffect, useRef } from "react";

type ModalProps = {
  children: React.ReactNode;
  isOpen: boolean;
  closeModal: () => void;
  size?: "sm" | "md" | "lg" | "xl";
  labelledById?: string;
};

const sizeClasses = {
  sm: "w-[400px]",
  md: "w-[512px]",
  lg: "w-[768px]",
  xl: "w-[1024px]",
};

export const Modal = ({
  children,
  isOpen,
  closeModal,
  size = "md",
  labelledById,
}: ModalProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Mantém o closeModal em ref para que o efeito de focus-trap dependa só de
  // `isOpen`. Sem isso, um closeModal recriado a cada render (comum) faz o efeito
  // rodar a cada tecla e roubar o foco de volta para o 1º elemento focável.
  const closeModalRef = useRef(closeModal);
  closeModalRef.current = closeModal;

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement;

    const getFocusable = () =>
      Array.from(
        containerRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) ?? []
        // Fora o que não está na tela (ex.: etapa escondida com `hidden`): senão
        // o "primeiro" da armadilha é um campo que não recebe foco e o
        // Shift+Tab escapa do modal.
      ).filter(el => el.getClientRects().length > 0);

    const focusable = getFocusable();
    focusable[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeModalRef.current();
        return;
      }
      if (e.key !== "Tab") return;

      const elements = getFocusable();
      if (elements.length === 0) return;

      const first = elements[0];
      const last = elements[elements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCloseModal = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    closeModal();
  };

  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center px-4">
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        className={`z-20 ${sizeClasses[size]} max-h-[90vh] max-w-[95vw] overflow-y-auto rounded-2xl bg-gray-50 p-4`}
      >
        {children}
      </div>
      <div
        onClick={handleCloseModal}
        className="absolute inset-0 bg-black/[0.4]"
      />
    </div>
  );
};
